import { logDividendClaimed } from "@app/lib/analytics";
import appDividendTrackerAbi from "@app/lib/contract/abis/dividendTracker.json";
import appTokenAbi from "@app/lib/contract/abis/mtvx.json";
import { AppTokenAddress, ETHDecimals } from "@app/shared/AppConstant";
import { BigNumberish } from "@ethersproject/bignumber";
import { Contract } from "@ethersproject/contracts";
import { JsonRpcSigner } from "@ethersproject/providers";
import { formatUnits } from "@ethersproject/units";
import { useWeb3React } from "@web3-react/core";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "./useSnackbar";
import { getPrice } from "./useTokenPrice";

interface WalletBalance {
  balance?: BigNumberish;
  formattedBalance: string;
  decimals: number;
  price: {
    bnb: string;
    token: string;
    usdt: string;
  };
}

type GetWalletBalance = (walletAddress: string) => Promise<WalletBalance>;

interface GetClaimableTokenResponse {
  claimableToken: BigNumberish;
  formattedClaimableToken: string;
}
type GetClaimableToken = (walletAddress: string) => Promise<GetClaimableTokenResponse | null>;

interface TotalDividendsClaimed {
  accumulativeDividendOfWallet: BigNumberish;
  formattedAccumulativeDividendOfWallet: string;
}

type GetTotalDividendsClaimed = (
  walletAddress: string,
  claimableToken: GetClaimableTokenResponse,
) => Promise<TotalDividendsClaimed | null>;

export default function useTokenContract(): {
  contract: Contract | null;
  getWalletBalance: GetWalletBalance;
  getClaimableToken: GetClaimableToken;
  getTotalDividendsClaimed: GetTotalDividendsClaimed;
  getTotalDividendsDistributed: () => Promise<void>;
  claimDividend: () => Promise<void>;
  totalDistributedETH: string;
  isLoading: boolean;
  totalDividendsClaimed: TotalDividendsClaimed | null;
  claimableToken: GetClaimableTokenResponse | null;
  walletBalance: WalletBalance | null;
  getAllContractInformation: (walletAddress: string) => Promise<void>;
  isClaiming: boolean;
} {
  const { active, account, library } = useWeb3React();
  const [tokenContract, setTokenContract] = useState<Contract | null>(null);
  const [totalDistributed, setTotalDistributed] = useState<string>("0");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalDividendsClaimed, setTotalDividendsClaimed] = useState<TotalDividendsClaimed | null>(null);
  const [claimableToken, setClaimableToken] = useState<GetClaimableTokenResponse | null>(null);
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);
  const [isClaiming, setIsClaiming] = useState<boolean>(false);
  const { snackbar } = useSnackbar();
  /**
   * Initialize Dashboard on load
   */
  const getAllContractInformation = async (walletAddress: string) => {
    setIsLoading(true);
    try {
      const promises = [];
      promises.push(getClaimableToken(walletAddress));
      promises.push(getWalletBalance(walletAddress));
      promises.push(getTotalDividendsDistributed());

      // @ts-ignore
      await Promise.all(promises);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (active) {
      try {
        const tokenContract = new Contract(AppTokenAddress, appTokenAbi, library.getSigner() as JsonRpcSigner);
        setTokenContract(tokenContract);
      } catch (error) {
        console.error(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, library]);

  const handleClaimDividend = useCallback(async () => {
    try {
      if (claimableToken?.formattedClaimableToken === "0") {
        return;
      }
      const tx = await tokenContract?.claim();
      setIsClaiming(true);
      await library.waitForTransaction(tx.hash);
      logDividendClaimed();
      setIsClaiming(false);
      if (account) {
        await getAllContractInformation(account);
      }
    } catch (error: any) {
      console.log(error);
      if (error?.code === 4001) {
        snackbar.show("Transaction Rejected", "error");
      }
      setIsClaiming(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, library, tokenContract]);

  const getTotalDividendsDistributed = useCallback(async () => {
    if (tokenContract) {
      try {
        const total = await tokenContract.getTotalDividendsDistributed();
        setTotalDistributed(Number(formatUnits(total, ETHDecimals)).toFixed(6).toString());
      } catch (error) {
        console.error("Error getting total distributed:", error);
      }
    }
  }, [tokenContract]);

  const getTotalDividendsClaimed: GetTotalDividendsClaimed = useCallback(
    async (walletAddress, claimableToken) => {
      if (tokenContract) {
        const dividendTrackerContractAddress = await tokenContract.dividendTracker();
        const dividendTrackerContract = new Contract(
          dividendTrackerContractAddress,
          appDividendTrackerAbi,
          library.getSigner(),
        );
        const accumulativeDividendOfWallet = await dividendTrackerContract.accumulativeDividendOf(walletAddress);

        setTotalDividendsClaimed({
          accumulativeDividendOfWallet: accumulativeDividendOfWallet,
          formattedAccumulativeDividendOfWallet: (
            Number(formatUnits(accumulativeDividendOfWallet, ETHDecimals)) -
            Number(claimableToken?.formattedClaimableToken)
          )
            .toFixed(6)
            .toString(),
        });

        return {
          accumulativeDividendOfWallet: accumulativeDividendOfWallet,
          formattedAccumulativeDividendOfWallet: (
            Number(formatUnits(accumulativeDividendOfWallet, ETHDecimals)) -
            Number(claimableToken?.formattedClaimableToken)
          )
            .toFixed(6)
            .toString(),
        };
      }
      return null;
    },
    [library, tokenContract],
  );

  const getClaimableToken: GetClaimableToken = async walletAddress => {
    if (tokenContract) {
      const claimableToken = await tokenContract.withdrawableDividendOf(walletAddress);
      setClaimableToken({
        claimableToken,
        formattedClaimableToken: Number(formatUnits(claimableToken, ETHDecimals)).toFixed(6).toString(),
      });
      await getTotalDividendsClaimed(walletAddress, {
        claimableToken,
        formattedClaimableToken: Number(formatUnits(claimableToken, ETHDecimals)).toFixed(6).toString(),
      });
      return {
        claimableToken,
        formattedClaimableToken: Number(formatUnits(claimableToken, ETHDecimals)).toFixed(6).toString(),
      };
    }
    return null;
  };

  const getWalletBalance: GetWalletBalance = async walletAddress => {
    if (tokenContract && library) {
      const balance = await tokenContract.balanceOf(walletAddress);
      const decimals = await tokenContract.decimals();
      const formattedBalance = formatUnits(balance, decimals);
      const { bnb, token, usdt } = await getPrice({
        amount: balance,
        signer: library.getSigner(),
        decimals,
        tokenAddress: AppTokenAddress,
      });
      setWalletBalance({
        balance,
        formattedBalance,
        decimals,
        price: {
          bnb,
          token,
          usdt,
        },
      });
      return {
        balance,
        formattedBalance,
        decimals,
        price: {
          bnb,
          token,
          usdt,
        },
      };
    }
    return {
      balance: undefined,
      formattedBalance: "0",
      decimals: 0,
      price: {
        bnb: "0",
        token: "0",
        usdt: "0",
      },
    };
  };

  return {
    contract: tokenContract,
    getWalletBalance,
    getClaimableToken,
    getTotalDividendsDistributed,
    getTotalDividendsClaimed,
    getAllContractInformation,
    claimDividend: handleClaimDividend,
    totalDistributedETH: totalDistributed,
    isLoading,
    totalDividendsClaimed,
    claimableToken,
    walletBalance,
    isClaiming,
  };
}
